import React from "react";
import { CalendarDays, CheckSquare, ClipboardCheck, FileText, BarChart3, Handshake, FileCheck2, DollarSign, Megaphone } from "lucide-react";

export const TILE_META = {
  calendar:    { label: "Calendar",          icon: CalendarDays,   to: "/calendar",            accent: "#2563EB", quick: { label: "New appointment", to: "/calendar?new=1" } },
  tasks:       { label: "Tasks",             icon: CheckSquare,    to: "/tasks",               accent: "#DC2626", quick: { label: "New task", to: "/tasks?new=1" } },
  onboarding:  { label: "Onboarding",        icon: ClipboardCheck, to: "/onboarding",          accent: "#0891B2" },
  proposals:   { label: "Proposals",         icon: FileText,       to: "/proposals",           accent: "#0B132B", quick: { label: "New proposal", to: "/proposals/new" } },
  proformas:   { label: "Proformas",         icon: BarChart3,      to: "/proforma",            accent: "#7C3AED" },
  agreements:  { label: "Dealer Agreements", icon: Handshake,      to: "/dealers/existing",    accent: "#D4AF37" },
  contracts:   { label: "Contracts",         icon: FileCheck2,     to: "/commissions",         accent: "#059669" },
  commissions: { label: "Commissions",       icon: DollarSign,     to: "/commissions",         accent: "#B8942F" },
  marketing:   { label: "Marketing",         icon: Megaphone,      to: "/marketing/materials", accent: "#EA580C" },
};

const fmt = (n) => (n == null ? "—" : typeof n === "string" ? n : Number(n).toLocaleString());
const timeOf = (iso, allDay) => (allDay ? "All day" : new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }));
const dayOf = (iso) => { const d = new Date(iso), t = new Date(); return d.toDateString() === t.toDateString() ? "Today" : d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }); };
const stage = (s) => (s || "").replace(/_/g, " ");

export const Stat = ({ label, value, tone, testId }) => (
  <div className="min-w-0" data-testid={testId}>
    <div className={`font-mono text-2xl font-black leading-none ${tone === "bad" ? "text-red-600" : tone === "good" ? "text-emerald-700" : "text-[#0B132B]"}`}>{fmt(value)}</div>
    <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D] mt-1 truncate">{label}</div>
  </div>
);

const Row = ({ left, right, sub, tone }) => (
  <li className="flex items-center justify-between gap-3 py-1.5 border-t border-[#F1F3F5] first:border-0 text-xs">
    <div className="min-w-0"><div className="truncate text-[#0B132B] font-medium">{left}</div>{sub && <div className="text-[10px] text-[#6C757D] truncate">{sub}</div>}</div>
    {right && <div className={`shrink-0 font-mono text-[11px] ${tone === "bad" ? "text-red-600 font-bold" : "text-[#6C757D]"}`}>{right}</div>}
  </li>
);

const Empty = ({ children }) => <div className="text-xs text-[#6C757D] italic py-2">{children}</div>;
const Unavailable = ({ reason }) => <div className="text-xs text-[#6C757D] italic py-2">Not connected{reason ? ` — ${reason}` : ""}.</div>;

const Calendar = ({ d, size }) => (
  <>
    <div className="grid grid-cols-2 gap-3"><Stat label="Today" value={d.today} testId="tile-calendar-today" /><Stat label="Next 7 days" value={d.week} /></div>
    {size !== "compact" && (d.upcoming?.length ? (
      <ul className="mt-3">{d.upcoming.slice(0, size === "expanded" ? 5 : 2).map((e) => <Row key={e.id + e.start} left={e.title} sub={e.dealer_name} right={`${dayOf(e.start)} · ${timeOf(e.start, e.all_day)}`} />)}</ul>
    ) : <Empty>No upcoming appointments.</Empty>)}
  </>
);

const Tasks = ({ d, size }) => (
  <>
    <div className="grid grid-cols-3 gap-3"><Stat label="Open" value={d.open} testId="tile-tasks-open" /><Stat label="Overdue" value={d.overdue} tone={d.overdue ? "bad" : undefined} testId="tile-tasks-overdue" /><Stat label="Due today" value={d.today} /></div>
    {size !== "compact" && (d.next?.length ? (
      <ul className="mt-3">{d.next.slice(0, size === "expanded" ? 5 : 2).map((t) => <Row key={t.id} left={t.title} sub={t.dealer_name} right={t.bucket === "overdue" ? "Overdue" : t.due_date || "No date"} tone={t.bucket === "overdue" ? "bad" : undefined} />)}</ul>
    ) : <Empty>Nothing needs attention.</Empty>)}
  </>
);

const Onboarding = ({ d, size }) => (
  <>
    <div className="grid grid-cols-3 gap-3"><Stat label="In pipeline" value={d.pipeline} testId="tile-onboarding-pipeline" /><Stat label="Overdue SLA" value={d.overdue} tone={d.overdue ? "bad" : undefined} /><Stat label="Installing" value={d.installing} /></div>
    {size !== "compact" && (
      <ul className="mt-3">
        {d.overdue_items?.map((o) => <Row key={o.id} left={o.dealer_name} sub={stage(o.status)} right="Overdue" tone="bad" />)}
        {size === "expanded" && d.install_items?.map((o) => <Row key={o.id} left={o.dealer_name} sub={stage(o.status)} right="Install" />)}
        {!d.overdue_items?.length && !(size === "expanded" && d.install_items?.length) && <Empty>No overdue or installing dealers.</Empty>}
      </ul>)}
  </>
);

const Proposals = ({ d, size }) => (
  <>
    <div className="grid grid-cols-3 gap-3"><Stat label="Total" value={d.total} testId="tile-proposals-total" /><Stat label="Last 30 days" value={d.last_30} /><Stat label="Converted" value={d.conversion_pct == null ? null : `${d.conversion_pct}%`} tone="good" /></div>
    {size !== "compact" && (d.recent?.length ? <ul className="mt-3">{d.recent.slice(0, size === "expanded" ? 4 : 2).map((p) => <Row key={p.id} left={p.title || "Untitled"} right={p.created_at?.slice(0, 10)} />)}</ul> : <Empty>No proposals yet.</Empty>)}
  </>
);

const Proformas = ({ d, size }) => (
  <>
    <div className="grid grid-cols-3 gap-3"><Stat label="Total" value={d.total} testId="tile-proformas-total" /><Stat label="Last 30 days" value={d.last_30} /><Stat label="Converted" value={d.conversion_pct == null ? null : `${d.conversion_pct}%`} tone="good" /></div>
    {size !== "compact" && (d.recent?.length ? <ul className="mt-3">{d.recent.slice(0, size === "expanded" ? 4 : 2).map((p) => <Row key={p.id} left={p.title || "Untitled"} right={p.created_at?.slice(0, 10)} />)}</ul> : <Empty>No proformas yet.</Empty>)}
  </>
);

const Agreements = ({ d, size }) => (
  <>
    <div className="grid grid-cols-4 gap-2"><Stat label="Total" value={d.total} testId="tile-agreements-total" /><Stat label="Pending" value={d.pending} tone={d.pending ? "bad" : undefined} /><Stat label="Signed" value={d.signed} /><Stat label="Executed" value={d.executed} tone="good" /></div>
    {size !== "compact" && (d.pending_items?.length ? <ul className="mt-3">{d.pending_items.map((p) => <Row key={p.id} left={p.dealer_name} right="Awaiting signature" tone="bad" />)}</ul> : <Empty>No agreements awaiting signature.</Empty>)}
  </>
);

const Contracts = ({ d, size }) => d.unavailable ? <Unavailable reason={d.reason} /> : (
  <>
    <div className="grid grid-cols-2 gap-3"><Stat label="Sold MTD" value={d.sold?.mtd} testId="tile-contracts-sold" /><Stat label="Remitted MTD" value={d.remitted?.mtd} tone="good" /></div>
    {size !== "compact" && <div className="grid grid-cols-2 gap-3 mt-3"><Stat label="Sold YTD" value={d.sold?.ytd} /><Stat label="Remitted YTD" value={d.remitted?.ytd} /></div>}
    {size === "expanded" && d.top?.length > 0 && <ul className="mt-3">{d.top.map((t) => <Row key={t.id || t.name} left={t.name} right={`${fmt(t.mtd)} MTD`} />)}</ul>}
  </>
);

const Commissions = ({ d }) => d.unavailable ? <Unavailable reason={d.reason} /> : (
  <div className="grid grid-cols-2 gap-3"><Stat label="Remitted MTD" value={d.remitted_mtd} testId="tile-commissions-remitted" /><Stat label="Projected EOM" value={d.eom_projection} tone="good" /></div>
);

const Marketing = ({ d }) => (
  <div className="grid grid-cols-3 gap-3"><Stat label="Materials" value={d.materials} testId="tile-marketing-materials" /><Stat label="My orders" value={d.my_orders} /><Stat label="In progress" value={d.open_orders} /></div>
);

const RENDER = { calendar: Calendar, tasks: Tasks, onboarding: Onboarding, proposals: Proposals, proformas: Proformas, agreements: Agreements, contracts: Contracts, commissions: Commissions, marketing: Marketing };

export const TileBody = ({ tileKey, data, size }) => {
  if (!data) return <div className="animate-pulse space-y-2"><div className="h-7 w-24 bg-[#F1F3F5] rounded-sm" /><div className="h-3 w-40 bg-[#F1F3F5] rounded-sm" /></div>;
  if (data.error) return <div className="text-xs text-red-600" data-testid={`tile-${tileKey}-error`}>Couldn't load — {data.error}</div>;
  const R = RENDER[tileKey];
  return R ? <R d={data} size={size} /> : null;
};
